import { useCallback, useRef } from "react";
import styles from "./styles/runner.module.css";
import type { MissionPayload, AttemptEnvelope } from "./types";
import { PrimitiveRunner } from "./runners/PrimitiveRunner";
import { CustomRunner } from "./runners/CustomRunner";
import { MdxRunner } from "./runners/MdxRunner";
import { newClientEventId, postAttempt } from "./bridge/postScore";

export interface MissionContext {
  /** Mission.public_id (UUID). */
  mission_public_id: string;
  /** MissionRevision.public_id (UUID). */
  mission_revision_public_id: string;
}

export interface MissionPlayerProps {
  payload: MissionPayload;
  context: MissionContext;
  /**
   * Optional explicit callback. If omitted, the player auto-emits the
   * envelope via `postAttempt` (which posts to the native host on iOS /
   * Android, or window.parent on browser).
   */
  onAttempt?: (envelope: AttemptEnvelope) => void;
}

/**
 * Top-level entry point. The runner dispatches by `payload.renderer`. New
 * renderers go here.
 *
 * Lifecycle:
 *   1) Host loads the iframe / WebView with this component mounted.
 *   2) Learner interacts with the widget.
 *   3) Widget calls `onScored({score, passed, raw})`.
 *   4) MissionPlayer wraps it into an AttemptEnvelope and dispatches via
 *      `postAttempt` (or the caller's `onAttempt`).
 *   5) Host re-emits to Nexus with the learner's bearer token.
 */
export function MissionPlayer({ payload, context, onAttempt }: MissionPlayerProps) {
  // Capture the mount time once. useRef lazy-init pattern keeps it monotonic
  // across re-renders without taking a state slot.
  const startedAtRef = useRef<number | null>(null);
  if (startedAtRef.current === null) startedAtRef.current = Date.now();

  const onScored = useCallback(
    (result: { score: number; passed: boolean; raw: unknown }) => {
      const startedAt = startedAtRef.current ?? Date.now();
      const envelope: AttemptEnvelope = {
        client_event_id: newClientEventId(),
        mission_public_id: context.mission_public_id,
        mission_revision_public_id: context.mission_revision_public_id,
        score: result.score,
        passed: result.passed,
        duration_ms: Math.max(0, Date.now() - startedAt),
        raw_response: result.raw,
      };
      if (onAttempt) onAttempt(envelope);
      else postAttempt(envelope);
    },
    [context.mission_public_id, context.mission_revision_public_id, onAttempt]
  );

  return (
    <div className={styles.shell}>
      {renderByPayload(payload, onScored)}
    </div>
  );
}

function renderByPayload(
  payload: MissionPayload,
  onScored: (result: { score: number; passed: boolean; raw: unknown }) => void
) {
  switch (payload.renderer) {
    case "primitive":
      return <PrimitiveRunner payload={payload} onScored={onScored} />;
    case "custom":
      return <CustomRunner payload={payload} onScored={onScored} />;
    case "mdx":
      return <MdxRunner payload={payload} onScored={onScored} />;
    case "unity":
      return (
        <NotForWeb
          headline="Esta misión se juega en AR (Unity)"
          subline={`unity_mission_id: ${payload.unity_mission_id}`}
        />
      );
    case "native":
      return (
        <NotForWeb
          headline="Esta misión se juega en el dispositivo"
          subline={`native_runner: ${payload.native_runner}`}
        />
      );
    default: {
      const exhaustive: never = payload;
      void exhaustive;
      return <div>Renderer no soportado.</div>;
    }
  }
}

function NotForWeb({ headline, subline }: { headline: string; subline: string }) {
  return (
    <div className={styles.summary}>
      <strong>{headline}</strong>
      <span style={{ color: "#4b5563", fontSize: 14 }}>{subline}</span>
      <span style={{ color: "#6b7280", fontSize: 13 }}>
        Abre la misión desde la app nativa para jugarla.
      </span>
    </div>
  );
}
